import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { cloneDeep } from 'lodash';
import { Button, Divider, Radio, Space, Form, message } from 'antd';
import {
  ErrorHandler,
  Drawer,
  LoadingState,
  SubmitButton,
} from '@common/components';
import { ApprovalsEditDrawerListApprovalChains } from './_approvals-edit-drawer-list-approval-chains';
import { ApprovalsEditDrawerDataBox } from './_approvals-edit-drawer-data-box';
import { useQuery, useMutation } from '@common/hooks';
import {
  ActionTypeTitlesType,
  ApprovalChainTemplateType,
  ApprovalsEditDrawerMutation,
  APPROVALS_ORGANIZATION_UNIT_QUERY,
  APPROVALS_UNIT_MEMBERSHIPS_QUERY,
} from '@approvals/graphql';
import {
  filteredApprovalChainTemplatesByActionType,
  getActionTypeTitle,
} from '@approvals/services';

export const ApprovalDrawerContext = React.createContext({
  forceUpdate: () => {},
  approvers: [] as { label: string; value: string }[],
  preApproved: false,
});

type Props = {
  onClose: () => void;
  open: boolean;
  unitId?: string;
  facilityName?: string;
  actionTypeKey?: ActionTypeTitlesType;
};

export const ApprovalsEditDrawer = ({
  open,
  onClose = () => {},
  unitId,
  facilityName: organizationBusinessName,
  actionTypeKey,
}: Props) => {
  const [, updateState] = useState<object>();
  const forceUpdate = useCallback(() => updateState({}), []);
  const [form] = Form.useForm();
  const [chains, setChains] = useState([]);
  const [preApproved, setPreApproved] = useState(false);

  const {
    data: organizationUnitData,
    loading: organizationUnitLoading,
    error: organizationUnitError,
  } = useQuery(APPROVALS_ORGANIZATION_UNIT_QUERY, {
    variables: {
      id: unitId || '',
    },
    skip: !unitId,
  });

  const unit = organizationUnitData?.organizationUnit;
  const preApprovedActions = unit?.preapprovalActions || [];
  const costCenter = unit?.costCenter || '';
  const unitName = unit?.name || '';
  const actionTypeTitle = actionTypeKey && getActionTypeTitle(actionTypeKey);
  const approvalChainTemplates = useMemo(() => {
    return actionTypeKey && unit?.approvalChainTemplates
      ? filteredApprovalChainTemplatesByActionType(
          unit.approvalChainTemplates,
          actionTypeKey
        )
      : [];
  }, [unit?.approvalChainTemplates, actionTypeKey]);

  const {
    data,
    error: unitMembershipsError,
    loading: unitMembershipsLoading,
  } = useQuery(APPROVALS_UNIT_MEMBERSHIPS_QUERY, {
    variables: {
      id: unitId || '',
    },
    skip: !unitId,
  });

  const approvers = buildApproversOptions(
    data?.organizationUnit.department.worksMemberships || []
  );

  useEffect(() => {
    const isPreApprovedValue = preApprovedActions?.includes(
      actionTypeKey as ActionTypeTitlesType
    );

    setPreApproved(!!isPreApprovedValue);

    if (open) {
      form.setFieldsValue({ preApproved: isPreApprovedValue });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preApprovedActions]);

  useEffect(() => {
    if (approvalChainTemplates) {
      setChains(cloneDeep(approvalChainTemplates as never[]));
    }
  }, [approvalChainTemplates]);

  const [createApprovalTemplate, { loading: mutationLoading }] = useMutation(
    ApprovalsEditDrawerMutation,
    {
      onCompleted: (response) => {
        if (response?.createApprovalTemplate?.errors) {
          message.error('Error occurred, try again later');
        } else {
          message.success('Approval Chain Templates updated');
        }

        onClose?.();
      },
      onError: () => message.error('Error occurred, try again later'),
      refetchQueries: ['OrganizationBusinessQuery'],
    }
  );

  if (organizationUnitError || unitMembershipsError) {
    return (
      <ErrorHandler
        error={organizationUnitError || unitMembershipsError}
        log="ApprovalsEditDrawer"
        messageType="contextual"
      />
    );
  }

  function onFinish() {
    const approvalChainTemplates = chains.map(
      (chain: ApprovalChainTemplateType) => ({
        id: chain.id,
        destroy: chain.destroy,
        approvalChainStepTemplates: chain.approvalChainStepTemplates.map(
          (step) => ({
            id: step.id,
            destroy: step.destroy,
            approvalChainApproverTemplates:
              step.approvalChainApproverTemplates.map((approver) => ({
                id: approver.id,
                worksMembershipId: approver.worksMembership.id,
                destroy: approver.destroy,
              })),
          })
        ),
      })
    );

    createApprovalTemplate({
      variables: {
        input: {
          organizationUnitId: unitId || '',
          actionType: actionTypeKey || '',
          approvalChainTemplates,
          preApproved,
        },
      },
    });
  }

  return (
    <ApprovalDrawerContext.Provider
      value={{ forceUpdate, approvers, preApproved }}
    >
      <Drawer
        className="approvals-edit-drawer"
        title={`Approval Chains: ${actionTypeTitle}`}
        open={open}
        onClose={onClose}
        footer={
          <Space size={16}>
            <Button size="large" onClick={onClose}>
              Cancel
            </Button>
            <SubmitButton onClick={form.submit} loading={mutationLoading}>
              Save
            </SubmitButton>
          </Space>
        }
      >
        {organizationUnitLoading || unitMembershipsLoading ? (
          <LoadingState />
        ) : (
          <>
            <p>
              {`Set up Approval Chains that can approve ${actionTypeTitle} for this
          unit.`}
            </p>
            <ApprovalsEditDrawerDataBox
              organizationBusinessName={organizationBusinessName || ''}
              unitName={unitName}
              costCenter={costCenter}
            />
            <Divider />
            <p className="text-heavy">{`Do ${actionTypeTitle} for this unit require approval?`}</p>
            <Form
              form={form}
              initialValues={{ preApproved }}
              onFinish={onFinish}
            >
              <Form.Item name="preApproved">
                <Radio.Group onChange={(e) => setPreApproved(e.target.value)}>
                  <Space direction="vertical">
                    <Radio value={false}>Yes, they require approval</Radio>
                    <Radio value={true}>No, they are pre-approved</Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>
              {!preApproved && (
                <ApprovalsEditDrawerListApprovalChains
                  approvalChainTemplates={chains}
                />
              )}
            </Form>
          </>
        )}
      </Drawer>
    </ApprovalDrawerContext.Provider>
  );
};

function buildApproversOptions(
  worksMemberships: { id: string; name: string }[]
) {
  return (
    worksMemberships.map((member: { id: string; name: string }) => ({
      label: member.name,
      value: member.id,
    })) || []
  );
}
